import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Search, X } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';
import { API_BASE_URL } from '../apiBase';
import ContactAvatar from './ContactAvatar';
import { useLanguage } from '../context/LanguageContext';

function digitsOnly(v) {
  return String(v || '').replace(/\D/g, '');
}

function isWeakName(name, phone) {
  const n = String(name || '').trim();
  if (!n) return true;
  const nd = digitsOnly(n);
  const pd = digitsOnly(phone);
  if (nd.length > 15 && /^\d+$/.test(n.replace(/\s+/g, ''))) return true;
  if (nd.length >= 8 && nd === digitsOnly(n.replace(/[+\s()-]/g, ''))) {
    if (n.length <= nd.length + 4) return true;
  }
  if (pd && nd === pd && pd.length >= 8) return true;
  return false;
}

function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const d = digitsOnly(phone);
  if (d.length > 15) return 'ID interno (aguardando número)';
  if (d.length >= 11 && d.startsWith('55')) {
    const rest = d.slice(2);
    if (rest.length === 11) return `+55 (${rest.slice(0, 2)}) ${rest.slice(2, 7)}-${rest.slice(7)}`;
    if (rest.length === 10) return `+55 (${rest.slice(0, 2)}) ${rest.slice(2, 6)}-${rest.slice(6)}`;
  }
  if (d.length >= 8) return `+${d}`;
  return d || String(phone);
}

function pickDisplayName(row) {
  const phone = row.phone || '';
  const name = String(row.name || '').trim();
  if (name && !isWeakName(name, phone)) return name;
  return name || formatPhoneDisplay(phone) || 'Sem nome';
}

/**
 * Busca contato CRM / atendimento recente e preenche nome + WhatsApp.
 */
export default function ContactQuickPick({
  apiUrl = API_BASE_URL,
  onPick,
  className,
  label,
  hint,
  maxItems = 80,
}) {
  const { t } = useLanguage();
  const labelText = label || t('cqp.label');
  const hintText = hint || t('cqp.hint');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const timer = useRef(null);
  const boxRef = useRef(null);

  const search = useCallback(
    async (term) => {
      setLoading(true);
      try {
        const params = {};
        if (term.trim()) params.search = term.trim();
        const convParams = term.trim()
          ? { tab: 'all', search: term.trim(), limit: 300 }
          : { tab: 'all', limit: 300 };
        const [contactsRes, convRes] = await Promise.all([
          axios.get(`${apiUrl}/api/contacts`, {
            params,
            withCredentials: true,
            timeout: 15000,
          }),
          axios
            .get(`${apiUrl}/api/conversations`, {
              params: convParams,
              withCredentials: true,
              timeout: 15000,
            })
            .catch(() => ({ data: [] })),
        ]);
        const list = Array.isArray(contactsRes.data)
          ? contactsRes.data
          : contactsRes.data?.contacts || [];
        const byPhone = new Map();
        const push = (row, tag) => {
          const phoneRaw = row.phone || '';
          const phone = digitsOnly(phoneRaw);
          const key = phone || row.id || `${row.name}`;
          if (!key) return;
          const prev = byPhone.get(key);
          if (prev && prev.tag === 'atendimento' && tag !== 'atendimento') return;
          const nextName = String(row.name || '').trim();
          const keepPrevName =
            prev &&
            !isWeakName(prev.name, prev.phone) &&
            isWeakName(nextName, phoneRaw || phone);
          byPhone.set(key, {
            id: row.id || prev?.id,
            name: keepPrevName ? prev.name : nextName || prev?.name || '',
            phone: phoneRaw || phone || prev?.phone || '',
            email: row.email || prev?.email || '',
            profilePic: row.profilePic || row.profile_picture || prev?.profilePic || '',
            tag: tag === 'atendimento' || prev?.tag === 'atendimento' ? 'atendimento' : tag,
            lastAt: row.lastAt || prev?.lastAt || '',
          });
        };
        const convs = Array.isArray(convRes.data)
          ? convRes.data
          : convRes.data?.conversations || [];
        for (const c of convs) {
          if (c.is_group) continue;
          push(
            {
              id: c.contact_id || c.id,
              name: c.contact_name || c.name,
              phone: c.contact_phone || c.phone,
              profilePic: c.contact_profile_picture || c.profile_picture,
              lastAt: c.last_message_at || '',
            },
            'atendimento',
          );
        }
        for (const c of list) {
          push(c, 'contato');
        }
        const merged = Array.from(byPhone.values()).sort((a, b) => {
          const aAtt = a.tag === 'atendimento' ? 0 : 1;
          const bAtt = b.tag === 'atendimento' ? 0 : 1;
          if (aAtt !== bAtt) return aAtt - bAtt;
          const at = String(a.lastAt || '');
          const bt = String(b.lastAt || '');
          if (at && bt && at !== bt) return bt.localeCompare(at);
          return String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR', {
            sensitivity: 'base',
          });
        });
        const cap = Math.max(20, Math.min(200, Number(maxItems) || 80));
        setItems(merged.slice(0, cap));
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl, maxItems],
  );

  useEffect(() => {
    if (!open) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => search(q), q.trim() ? 220 : 0);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [q, open, search]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!boxRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (c) => {
    const displayName = pickDisplayName(c);
    const phone = digitsOnly(c.phone) || c.phone;
    onPick?.({
      ...c,
      name: isWeakName(c.name, c.phone) ? '' : displayName,
      phone,
      displayName,
    });
    setQ('');
    setOpen(false);
    setItems([]);
  };

  return (
    <div ref={boxRef} className={cn('relative space-y-1.5', className)}>
      <Label className="text-[12px]">{labelText}</Label>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
            if (!items.length) search(q);
          }}
          placeholder={t('cqp.searchPh')}
          className="h-10 pl-9 pr-8"
          autoComplete="off"
        />
        {q ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            onClick={() => {
              setQ('');
              setItems([]);
            }}
            aria-label={t('cqp.clear')}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground leading-snug">{hintText}</p>

      {open ? (
        <div className="absolute z-50 mt-1 max-h-80 w-full overflow-y-auto rounded-[0.2rem] border border-border bg-popover shadow-lg">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-4 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> {t('cqp.searching')}
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {t('cqp.empty')}
            </p>
          ) : (
            <ul className="py-1">
              {items.map((c) => {
                const title = pickDisplayName(c);
                const phoneLabel = formatPhoneDisplay(c.phone);
                return (
                  <li key={c.id || `${c.phone}-${c.name}`}>
                    <button
                      type="button"
                      className="flex w-full items-start gap-2.5 px-3 py-2 text-left hover:bg-muted/60"
                      onClick={() => pick(c)}
                    >
                      <ContactAvatar
                        name={title}
                        phone={c.phone}
                        profilePic={c.profilePic}
                        contactId={c.id}
                        size="sm"
                        className="mt-0.5 shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5">
                          <span className="block truncate text-[13px] font-semibold text-foreground">
                            {title}
                          </span>
                          {c.tag === 'atendimento' ? (
                            <span className="shrink-0 rounded-[0.15rem] bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
                              Em atendimento
                            </span>
                          ) : null}
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {phoneLabel || 'Sem WhatsApp'}
                          {c.email ? ` · ${c.email}` : ''}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
